import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Box,
  useTheme,
  useMediaQuery,
  Divider
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Create,
  Dashboard,
  ExitToApp,
  ContactSupport,
  Event,
  Article,
  People,
  EmojiEvents as TrophyIcon,
  Gavel as GavelIcon,
  ClearAll as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clearAllCache } from '../../utils/cacheUtils';

const Header = ({ onMenuToggle }) => {
  const navigate = useNavigate();
  const { user, isAuthenticated, login, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [anchorEl, setAnchorEl] = useState(null);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSignIn = async () => {
    try {
      await login();
    } catch (error) {
      console.error('Error signing in:', error);
    }
  };

  const handleClearCache = async () => {
    handleMenuClose();
    try {
      const success = await clearAllCache();
      if (success) {
        alert('Cache cleared successfully! The page will reload.');
        window.location.reload();
      } else {
        alert('Cache clearing had some issues. Check console for details.');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      alert('Failed to clear cache. Check console for details.');
    }
  };

  const handleSignOut = async () => {
    handleMenuClose();
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const handleProfile = () => {
    handleMenuClose();
    navigate('/profile');
  };

  const handleMyStories = () => {
    handleMenuClose();
    navigate('/my-stories');
  };

  const handleSupport = () => {
    handleMenuClose();
    navigate('/support');
  };

  const handleAdmin = () => {
    handleMenuClose();
    navigate('/admin');
  };

  const handleManageEvents = () => {
    handleMenuClose();
    navigate('/admin/events');
  };

  const handleManageStories = () => {
    handleMenuClose();
    navigate('/admin/stories');
  };

  const handleManageJudges = () => {
    handleMenuClose();
    navigate('/admin/judges');
  };

  const handleJudgeEvents = () => {
    handleMenuClose();
    navigate('/judge/events');
  };

  // Check for admin role
  const isAdmin = user?.isAdmin || user?.role === 'admin';
  
  // Check for judge role (judges and admins can judge)
  const canJudge = user?.role === 'judge' || user?.role === 'admin';
  
  // Debug logging
  console.log('Header - User object:', user);
  console.log('Header - user?.isAdmin:', user?.isAdmin);
  console.log('Header - user?.role:', user?.role);
  console.log('Header - isAdmin result:', isAdmin);

  return (
    <AppBar position="sticky" elevation={1} sx={{ borderRadius: 0 }}>
      <Toolbar>
        {isMobile && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={onMenuToggle}
            sx={{ mr: 2 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        
        <Typography
          variant="h6"
          component="div"
          sx={{ 
            flexGrow: 1, 
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
          onClick={() => navigate('/')}
        >
          Short Stories
        </Typography>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: 2, mr: 2 }}>
            <Button 
              color="inherit" 
              onClick={() => navigate('/')}
              sx={{ borderRadius: 0 }}
            >
              Home
            </Button>
            {isAuthenticated && (
              <Button 
                color="inherit" 
                onClick={() => navigate('/stories')}
                sx={{ borderRadius: 0 }}
              >
                Browse Stories
              </Button>
            )}
           {isAuthenticated && (
            <Button 
              color="inherit" 
              onClick={() => navigate('/events')}
              sx={{ borderRadius: 0 }}
            >
              Events
            </Button>
           )}
           {isAuthenticated && (
            <Button 
              color="inherit" 
              onClick={() => navigate('/results')}
              sx={{ borderRadius: 0 }}
            >
              Results
            </Button>
           )}
           {isAuthenticated && (
            <Button 
              color="inherit" 
              onClick={() => navigate('/faq')}
              sx={{ borderRadius: 0 }}
            >
              FAQ
            </Button>
            )}
            {isAuthenticated && (
              <>
                <Button
                  color="inherit"
                  startIcon={<Create />}
                  onClick={() => navigate('/create')}
                  sx={{ borderRadius: 0 }}
                >
                  Write Story
                </Button>
                <Button 
                  color="inherit" 
                  onClick={() => navigate('/my-stories')}
                  sx={{ borderRadius: 0 }}
                >
                  My Stories
                </Button>
              </>
            )}
          </Box>
        )}

        {isAuthenticated ? (
          <>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              {(() => {
                const pictureUrl = user?.picture || user?.attributes?.picture;
                console.log('Final picture URL:', pictureUrl);
                
                return pictureUrl ? (
                  <Avatar 
                    src={pictureUrl} 
                    sx={{ width: 32, height: 32 }}
                    onError={(e) => {
                      console.error('Avatar image failed to load:', e);
                      e.target.style.display = 'none';
                    }}
                    onLoad={() => {
                      console.log('Avatar image loaded successfully');
                    }}
                  >
                    {/* Fallback content if image fails to load */}
                    <AccountCircle />
                  </Avatar>
                ) : (
                  <AccountCircle />
                );
              })()}
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleProfile}>
                <AccountCircle sx={{ mr: 1 }} />
                Profile
              </MenuItem>
              <MenuItem onClick={handleMyStories}>
                <Create sx={{ mr: 1 }} />
                My Stories
              </MenuItem>
              <MenuItem onClick={handleSupport}>
                <ContactSupport sx={{ mr: 1 }} />
                Support
              </MenuItem>
              {canJudge && (
                <MenuItem onClick={handleJudgeEvents}>
                  <GavelIcon sx={{ mr: 1 }} />
                  Judge Events
                </MenuItem>
              )}
              {isAdmin && (
                <>
                  <MenuItem onClick={handleAdmin}>
                    <People sx={{ mr: 1 }} />
                    Manage Users
                  </MenuItem>
                  <MenuItem onClick={handleManageStories}>
                    <Article sx={{ mr: 1 }} />
                    Manage Stories
                  </MenuItem>
                  <MenuItem onClick={handleManageEvents}>
                    <Event sx={{ mr: 1 }} />
                    Manage Events
                  </MenuItem>
                  <MenuItem onClick={handleManageJudges}>
                    <GavelIcon sx={{ mr: 1 }} />
                    Manage Judges
                  </MenuItem>
                </>
              )}
              <Divider />
              {/* <MenuItem onClick={handleClearCache}>
                <ClearIcon sx={{ mr: 1 }} />
                Clear Cache
              </MenuItem> */}
              <MenuItem onClick={handleSignOut}>
                <ExitToApp sx={{ mr: 1 }} />
                Sign Out
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Button
            color="inherit"
            variant="outlined"
            onClick={handleSignIn}
            sx={{ ml: 2, borderRadius: 0 }}
          >
            Sign In with Google
          </Button>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Header;
